import { createAsyncThunk } from '@reduxjs/toolkit';
import { billingService } from '@/services/billing.service';
import type {
  AcceptSubscriptionPauseInput,
  CancelSubscriptionInput,
  CreateSubscriptionInput,
  DowngradeImpactPreviewDto,
  StripeAddressDto,
  StripeAddressInput,
  StripeCheckoutDto,
  StripeCheckoutInput,
  StripeInvoiceDto,
  StripePaymentMethodDto,
  StripePriceDto,
  StripePromoCodeDto,
  StripeSetupIntentDto,
  StripeSubscriptionDto,
  StripeSubscriptionPaymentResumeDto,
  StripeSubscriptionResponseDto,
  StripeSubscriptionUpgradeInput,
  StripeUpgradePreviewDto,
  SubscriptionExitFlowDto,
  WorkspaceUsageDto,
} from '@/features/billing/types';
import type { AppError } from '@/features/auth/types';
import { mapApiError } from '@/lib/mapApiError';
import { resolveApiErrorCode } from '@/lib/subscriptionPaymentResume';

const toAppError = (err: unknown): AppError => {
  const e = err as { code?: string; message?: string; status?: number; details?: unknown };
  return {
    code: e.code ?? 'UNKNOWN',
    message: mapApiError(e.code ?? 'UNKNOWN', e.status),
    status: e.status,
    details: e.details,
    apiCode: resolveApiErrorCode(e) ?? undefined,
  };
};

export const upgradeSubscription = createAsyncThunk<
  StripeSubscriptionResponseDto,
  { workspaceId: number; input: StripeSubscriptionUpgradeInput },
  { rejectValue: AppError }
>('billing/upgradeSubscription', async ({ workspaceId, input }, { rejectWithValue }) => {
  try {
    return await billingService.upgradeSubscription(workspaceId, input);
  } catch (err) {
    return rejectWithValue(toAppError(err));
  }
});

export const fetchSubscription = createAsyncThunk<
  StripeSubscriptionDto,
  number,
  { rejectValue: AppError }
>('billing/fetchSubscription', async (workspaceId, { rejectWithValue }) => {
  try {
    return await billingService.getSubscription(workspaceId);
  } catch (err) {
    return rejectWithValue(toAppError(err));
  }
});

export const fetchUsage = createAsyncThunk<WorkspaceUsageDto, number, { rejectValue: AppError }>(
  'billing/fetchUsage',
  async (workspaceId, { rejectWithValue }) => {
    try {
      return await billingService.getUsage(workspaceId);
    } catch (err) {
      return rejectWithValue(toAppError(err));
    }
  },
);

export const fetchUpgradePreview = createAsyncThunk<
  StripeUpgradePreviewDto,
  { workspaceId: number; newPriceId: string },
  { rejectValue: AppError }
>('billing/fetchUpgradePreview', async ({ workspaceId, newPriceId }, { rejectWithValue }) => {
  try {
    return await billingService.getUpgradePreview(workspaceId, newPriceId);
  } catch (err) {
    return rejectWithValue(toAppError(err));
  }
});

export const fetchDowngradeImpactPreview = createAsyncThunk<
  DowngradeImpactPreviewDto,
  { workspaceId: number; newPriceId: string },
  { rejectValue: AppError }
>(
  'billing/fetchDowngradeImpactPreview',
  async ({ workspaceId, newPriceId }, { rejectWithValue }) => {
    try {
      return await billingService.getDowngradeImpactPreview(workspaceId, newPriceId);
    } catch (err) {
      return rejectWithValue(toAppError(err));
    }
  },
);

export const fetchPrices = createAsyncThunk<
  StripePriceDto[],
  { workspaceId: number; promoCode?: string },
  { rejectValue: AppError }
>('billing/fetchPrices', async ({ workspaceId, promoCode }, { rejectWithValue }) => {
  try {
    return await billingService.getPrices(workspaceId, promoCode);
  } catch (err) {
    return rejectWithValue(toAppError(err));
  }
});

export const fetchPromoCode = createAsyncThunk<
  StripePromoCodeDto,
  { workspaceId: number; code: string },
  { rejectValue: AppError }
>('billing/fetchPromoCode', async ({ workspaceId, code }, { rejectWithValue }) => {
  try {
    return await billingService.getPromoCode(workspaceId, code);
  } catch (err) {
    return rejectWithValue(toAppError(err));
  }
});

export const fetchPaymentMethods = createAsyncThunk<
  StripePaymentMethodDto[],
  number,
  { rejectValue: AppError }
>('billing/fetchPaymentMethods', async (workspaceId, { rejectWithValue }) => {
  try {
    return await billingService.getPaymentMethods(workspaceId);
  } catch (err) {
    return rejectWithValue(toAppError(err));
  }
});

export const fetchInvoices = createAsyncThunk<
  StripeInvoiceDto[],
  number,
  { rejectValue: AppError }
>('billing/fetchInvoices', async (workspaceId, { rejectWithValue }) => {
  try {
    return await billingService.getInvoices(workspaceId);
  } catch (err) {
    return rejectWithValue(toAppError(err));
  }
});

export const downloadInvoicesZip = createAsyncThunk<
  Blob,
  { workspaceId: number; invoiceIds: string[] },
  { rejectValue: AppError }
>('billing/downloadInvoicesZip', async ({ workspaceId, invoiceIds }, { rejectWithValue }) => {
  try {
    return await billingService.downloadInvoicesZip(workspaceId, invoiceIds);
  } catch (err) {
    return rejectWithValue(toAppError(err));
  }
});

export const fetchAddress = createAsyncThunk<StripeAddressDto, number, { rejectValue: AppError }>(
  'billing/fetchAddress',
  async (workspaceId, { rejectWithValue }) => {
    try {
      return await billingService.getAddress(workspaceId);
    } catch (err) {
      return rejectWithValue(toAppError(err));
    }
  },
);

export const updateAddress = createAsyncThunk<
  StripeAddressDto,
  { workspaceId: number; input: StripeAddressInput },
  { rejectValue: AppError }
>('billing/updateAddress', async ({ workspaceId, input }, { rejectWithValue }) => {
  try {
    await billingService.updateAddress(workspaceId, input);
    return await billingService.getAddress(workspaceId);
  } catch (err) {
    return rejectWithValue(toAppError(err));
  }
});

export const setPaymentMethodDefault = createAsyncThunk<
  void,
  { workspaceId: number; paymentMethodId: string },
  { rejectValue: AppError }
>(
  'billing/setPaymentMethodDefault',
  async ({ workspaceId, paymentMethodId }, { rejectWithValue }) => {
    try {
      await billingService.setPaymentMethodDefault(workspaceId, paymentMethodId);
    } catch (err) {
      return rejectWithValue(toAppError(err));
    }
  },
);

export const detachPaymentMethod = createAsyncThunk<
  void,
  { workspaceId: number; paymentMethodId: string },
  { rejectValue: AppError }
>('billing/detachPaymentMethod', async ({ workspaceId, paymentMethodId }, { rejectWithValue }) => {
  try {
    await billingService.detachPaymentMethod(workspaceId, paymentMethodId);
  } catch (err) {
    return rejectWithValue(toAppError(err));
  }
});

export const createSetupIntent = createAsyncThunk<
  StripeSetupIntentDto,
  number,
  { rejectValue: AppError }
>('billing/createSetupIntent', async (workspaceId, { rejectWithValue }) => {
  try {
    return await billingService.createSetupIntent(workspaceId);
  } catch (err) {
    return rejectWithValue(toAppError(err));
  }
});

export const createCheckout = createAsyncThunk<
  StripeCheckoutDto,
  { workspaceId: number; input: StripeCheckoutInput },
  { rejectValue: AppError }
>('billing/createCheckout', async ({ workspaceId, input }, { rejectWithValue }) => {
  try {
    return await billingService.createCheckout(workspaceId, input);
  } catch (err) {
    return rejectWithValue(toAppError(err));
  }
});

export const createSubscription = createAsyncThunk<
  StripeSubscriptionResponseDto,
  { workspaceId: number; input: CreateSubscriptionInput },
  { rejectValue: AppError }
>('billing/createSubscription', async ({ workspaceId, input }, { rejectWithValue }) => {
  try {
    return await billingService.createSubscription(workspaceId, input);
  } catch (err) {
    return rejectWithValue(toAppError(err));
  }
});

/**
 * Reprise du paiement d'une souscription déjà créée chez Stripe. À ne pas confondre avec
 * `resumeSubscription`, qui relance un abonnement annulé ou mis en pause.
 */
export const resumeSubscriptionPayment = createAsyncThunk<
  StripeSubscriptionPaymentResumeDto,
  number,
  { rejectValue: AppError }
>('billing/resumeSubscriptionPayment', async (workspaceId, { rejectWithValue }) => {
  try {
    return await billingService.resumeSubscriptionPayment(workspaceId);
  } catch (err) {
    return rejectWithValue(toAppError(err));
  }
});

export const resumeSubscription = createAsyncThunk<
  StripeSubscriptionDto,
  number,
  { rejectValue: AppError }
>('billing/resumeSubscription', async (workspaceId, { rejectWithValue }) => {
  try {
    return await billingService.resumeSubscription(workspaceId);
  } catch (err) {
    return rejectWithValue(toAppError(err));
  }
});

export const resumeSubscriptionPause = createAsyncThunk<
  StripeSubscriptionDto,
  number,
  { rejectValue: AppError }
>('billing/resumeSubscriptionPause', async (workspaceId, { rejectWithValue }) => {
  try {
    return await billingService.resumeSubscriptionPause(workspaceId);
  } catch (err) {
    return rejectWithValue(toAppError(err));
  }
});

export const startSubscriptionExitFlow = createAsyncThunk<
  SubscriptionExitFlowDto,
  number,
  { rejectValue: AppError }
>('billing/startSubscriptionExitFlow', async (workspaceId, { rejectWithValue }) => {
  try {
    return await billingService.startSubscriptionExitFlow(workspaceId);
  } catch (err) {
    return rejectWithValue(toAppError(err));
  }
});

export const acceptRetentionDowngrade = createAsyncThunk<
  SubscriptionExitFlowDto,
  number,
  { rejectValue: AppError }
>('billing/acceptRetentionDowngrade', async (workspaceId, { rejectWithValue }) => {
  try {
    return await billingService.acceptRetentionDowngrade(workspaceId);
  } catch (err) {
    return rejectWithValue(toAppError(err));
  }
});

export const declineRetentionDowngrade = createAsyncThunk<
  SubscriptionExitFlowDto,
  number,
  { rejectValue: AppError }
>('billing/declineRetentionDowngrade', async (workspaceId, { rejectWithValue }) => {
  try {
    return await billingService.declineRetentionDowngrade(workspaceId);
  } catch (err) {
    return rejectWithValue(toAppError(err));
  }
});

export const acceptRetentionPause = createAsyncThunk<
  SubscriptionExitFlowDto,
  { workspaceId: number; input: AcceptSubscriptionPauseInput },
  { rejectValue: AppError }
>('billing/acceptRetentionPause', async ({ workspaceId, input }, { rejectWithValue }) => {
  try {
    return await billingService.acceptRetentionPause(workspaceId, input);
  } catch (err) {
    return rejectWithValue(toAppError(err));
  }
});

export const declineRetentionPause = createAsyncThunk<
  SubscriptionExitFlowDto,
  number,
  { rejectValue: AppError }
>('billing/declineRetentionPause', async (workspaceId, { rejectWithValue }) => {
  try {
    return await billingService.declineRetentionPause(workspaceId);
  } catch (err) {
    return rejectWithValue(toAppError(err));
  }
});

export const confirmRetentionCancellation = createAsyncThunk<
  SubscriptionExitFlowDto,
  { workspaceId: number; input: CancelSubscriptionInput },
  { rejectValue: AppError }
>('billing/confirmRetentionCancellation', async ({ workspaceId, input }, { rejectWithValue }) => {
  try {
    return await billingService.confirmRetentionCancellation(workspaceId, input);
  } catch (err) {
    return rejectWithValue(toAppError(err));
  }
});
